import { Workspace } from '@/features/iam/workspaces/Workspace'
import type { WorkspaceSearcher } from '@/features/iam/workspaces/WorkspaceSearcher'
import type {
  GetCurrentUserWorkspaceMembershipsFn,
  GetTeamMembersForWorkspaceMemberFn,
} from '@/features/iam'
import { action, computed, observable } from 'mobx'
import { fromStream } from '@/utils/mobx-util'
import { Subject } from 'rxjs'
import { orderBy } from 'lodash-es'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

/**
 * View model for managing the firm dashboard workspace selector.
 */
export class FirmDashboardWorkspaceSearchViewModel
  implements WorkspaceSearcher, ActivationLifecycle
{
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Search text changes.
   */
  private readonly _searchTextChange = new Subject<string>()

  /**
   * The search text as a computed, derived from the search text change stream.
   */
  private readonly _getSearchText = fromStream('', this._searchTextChange)

  /**
   * User is searching a workspace.
   */
  @observable
  accessor editing = false

  /**
   * Workspace to filter by.
   */
  @observable
  accessor workspace: Workspace = Workspace.none

  constructor(
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _getTeamMembersForWorkspaceMember: GetTeamMembersForWorkspaceMemberFn,
  ) {}

  /**
   * Show workspace searcher if user can view dashboard in 5 or more workspaces.
   */
  @computed
  get showWorkspaceSearcher() {
    return this.workspacesWhereUserCanViewDashboard.length >= 5
  }

  /**
   * Search text computed getter.
   */
  @computed
  get searchText() {
    return this._getSearchText()
  }

  /**
   * Whether the current user have the permission
   * to view the Dashboard on the workspaces he's member of.
   */
  @computed
  get workspacesWhereUserCanViewDashboard() {
    return orderBy(
      this._getCurrentUserWorkspaceMemberships()
        .filter(({ member, workspace }) => {
          return (
            member.permissions.viewFirmDashboard ||
            this._getTeamMembersForWorkspaceMember(workspace.id).some(
              (tm) => tm.permissions.viewFirmDashboard,
            )
          )
        })
        .map((m) => m.workspace),
      (ws) => ws.name.toLowerCase(),
      'asc',
    )
  }

  /**
   * Filtered workspaces.
   */
  @computed
  get filteredWorkspaces() {
    const result = this.workspacesWhereUserCanViewDashboard

    if (this.searchText) {
      const searchText = this.searchText.toLowerCase()

      return result.filter((ws) =>
        ws.name.toLocaleLowerCase().includes(searchText),
      )
    }
    return result
  }

  /**
   * Sets the search text.
   *
   * @param searchText
   */
  @action.bound
  setSearchText(searchText: string) {
    this._searchTextChange.next(searchText)
  }

  /**
   * Selects the workspace to filter by.
   */
  @action.bound
  setWorkspace(workspace: Workspace) {
    this.workspace = workspace
  }

  /**
   * Starts searching for a workspace.
   */
  @action.bound
  startEditing() {
    this.editing = true
  }

  /**
   * Stops searching for a workspace.
   */
  @action.bound
  stopEditing() {
    this.editing = false
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }
}
